// Amplitude tracking for the sign-up flow, ported from the proto's tracking.js.
//
// Pipeline: every event POSTs to /api/tracking, which whitelists server-side
// (TrackEvent → AmplitudeEventJob); the `signup` event additionally fans out to
// Gist (handled server-side). No NEW events are introduced — we reuse the modal
// tunnel's event names at the equivalent chatbot moments (product constraint).
//
// Payload fix vs the proto: the live Rails controller (Api::TrackingController#create)
// reads `params[:properties]` and derives the device id server-side (unique_device_id),
// so we send { event_name, properties } — NOT the proto's { device_id, event_properties }.
// Fire-and-forget: failures never block or surface in the UI.
import { createPublicClient, type PublicApiClient } from '@twg/api-client'
import { ENDPOINTS } from '../config/endpoints'
import { getFunnelVariant } from './useFunnelVariant'

// Local whitelist (mirrors the server's ONLY_AMPLITUDE / BOTH_TRACKING lists) —
// guards against typos in dev. The real enforcement is server-side.
export const SIGNUP_EVENT_NAMES: readonly string[] = [
  'saw-registration-page',
  'finished-signup-step-universe-choice',
  'finished-signup-step-listing-type',
  'finished-signup-step-organisation-type',
  'finished-signup-step-address',
  'finished-signup-step-care-type',
  'finished-signup-step-registration',
  'finished-signup-step-exit-already-have-account',
  'finished-signup-step-exit-resume-registration',
  'signup', // the only event that also fans out to Gist (server-side)
]

// A plain snapshot of the orchestrator's answer state.
export interface SignupState {
  universe?: string | null
  firstInterest?: string | null
  role?: string | null
  subProfile?: string | null
  typeGarde?: string | null
  authMethod?: string | null
}

export interface SignupProperties {
  brand: string
  brand_country: string
  universe: string | null
  first_interest: string | null
  category: string
  role: string | null
  organisation: string | null
  care_type: string | null
  sub_type: null
  auth_method: string | null
}

interface AmplitudeIdentify {
  set(key: string, value: string): void
}
interface AmplitudeLike {
  Identify: new () => AmplitudeIdentify
  identify(identify: AmplitudeIdentify): void
}

// Proto role vocabulary → Amplitude tunnel vocabulary.
export function mapRoleForAmplitude(role?: string | null): string | null {
  if (!role) return null
  if (role === 'family' || role === 'employer') return 'employer'
  if (role === 'childminder' || role === 'employee') return 'employee'
  if (role === 'other') return 'organisation'
  return role
}

// The common sign_up event-properties set, built from a plain state snapshot.
// `previously_seen_as_user*` are computed server-side via the cookie.
export function buildSignupProperties(state: SignupState = {}): SignupProperties {
  return {
    brand: 'nounou-top', // V1 hardcoded
    brand_country: 'FR', // V1 hardcoded
    universe: state.universe || null,
    first_interest: state.firstInterest || null,
    category: 'child', // V1 Nounou-Top: enfant
    role: mapRoleForAmplitude(state.role),
    organisation: state.subProfile || null,
    care_type: state.typeGarde || null,
    sub_type: null, // V1 optional, unused
    auth_method: state.authMethod || null,
  }
}

// Fire-and-forget POST over the shared same-origin client (session cookie + CSRF token are its
// job — the server derives device id + current_user from the cookie). Resolves (undefined)
// regardless of network outcome.
async function post(client: PublicApiClient, url: string, payload: unknown): Promise<void> {
  try {
    await client.postSession(url, payload)
  } catch {
    // dev/staging or offline: silent — tracking must never block the funnel
  }
}

export function useSignupTracking() {
  const client = createPublicClient()

  // Emit a whitelisted event with `funnel_variant` auto-attached.
  function track(eventName: string, properties: Record<string, unknown> = {}): Promise<void> {
    if (!SIGNUP_EVENT_NAMES.includes(eventName)) {
      console.warn('[sign-up tracking] event not on local whitelist:', eventName)
      return Promise.resolve()
    }
    return post(client, ENDPOINTS.tracking, {
      event_name: eventName,
      properties: { ...properties, funnel_variant: getFunnelVariant() },
    })
  }

  // Convenience: emit a step event with the full buildSignupProperties payload.
  function trackStep(eventName: string, state: SignupState): Promise<void> {
    return track(eventName, { ...buildSignupProperties(state) })
  }

  // Account-created tracking (mirrors the proto's finishSignupFlow): the `signup`
  // event carries SPECIAL properties (not buildSignupProperties), followed by the
  // registration event with the full payload.
  function trackSignupSuccess(state: SignupState = {}): Promise<unknown> {
    return Promise.all([
      track('signup', {
        universe_choice: state.universe || null,
        ad_type: null, // V1: no explicit ad_type
        care_type: state.typeGarde || null,
      }),
      track('finished-signup-step-registration', { ...buildSignupProperties(state) }),
    ])
  }

  // Set the `signup_funnel_variant` user property through the Amplitude browser SDK when
  // the page carries it. Call once on arrival. There is no server-side identify route, so
  // the SDK is the only channel; without it this is a no-op.
  function identify(): Promise<void> {
    const amplitude = (window as unknown as { amplitude?: AmplitudeLike }).amplitude
    if (typeof window !== 'undefined' && amplitude?.Identify) {
      try {
        const id = new amplitude.Identify()
        id.set('signup_funnel_variant', getFunnelVariant())
        amplitude.identify(id)
      } catch {
        // silent
      }
    }
    return Promise.resolve()
  }

  return { track, trackStep, trackSignupSuccess, identify }
}
